import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Spinner,
  Text,
  Badge,
  IconButton,
  HStack,
  VStack
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { RefreshIcon, SearchIcon } from "../../utils/icons";
import { jwtDecode } from "jwt-decode";

// Ícones locais (não exportados por utils/icons)
const AddIcon = (props) => (
  <Box as="svg" viewBox="0 0 24 24" boxSize="18px" {...props} xmlns="http://www.w3.org/2000/svg">
    <path fill="currentColor" d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
  </Box>
);

const RemoveIcon = (props) => (
  <Box as="svg" viewBox="0 0 24 24" boxSize="18px" {...props} xmlns="http://www.w3.org/2000/svg">
    <path fill="currentColor" d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
  </Box>
);

const EditIcon = (props) => (
  <Box as="svg" viewBox="0 0 24 24" boxSize="18px" {...props} xmlns="http://www.w3.org/2000/svg">
    <path fill="currentColor" d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z" />
  </Box>
);

const PLATES_PER_PAGE = 20;

const RegisteredPlatesTab = () => {
  const [plates, setPlates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Modal estados
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();

  const [newPlate, setNewPlate] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newVehicleType, setNewVehicleType] = useState("");

  const [editingPlate, setEditingPlate] = useState(null);
  const [plateToRemove, setPlateToRemove] = useState(null);

  const cancelRef = useRef();
  const toast = useToast();

  const getUserIdFromToken = () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const decoded = jwtDecode(token);
      return decoded.id || decoded.userId || null;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  useEffect(() => {
    fetchPlates();
  }, []);

  const fetchPlates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/plates', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erro ao buscar placas');
      const data = await res.json();
      setPlates(data);
    } catch (error) {
      toast({ title: 'Erro', description: error.message, status: 'error', duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      const token = localStorage.getItem('token');
      const body = { plateNumber: newPlate.toUpperCase().trim(), description: newDescription, vehicleType: newVehicleType };
      const res = await fetch('/api/plates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error('Erro ao adicionar placa');
      const created = await res.json();
      setPlates(prev => [created, ...prev]);
      setCurrentPage(1);
      setNewPlate(''); setNewDescription(''); setNewVehicleType('');
      onClose();
      toast({ title: 'Placa adicionada', status: 'success', duration: 2000, isClosable: true });
    } catch (error) {
      toast({ title: 'Erro', description: error.message, status: 'error', duration: 3000, isClosable: true });
    }
  };

  const startEdit = (plate) => {
    setEditingPlate({ ...plate });
  };

  const handleEdit = async () => {
    if (!editingPlate) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/plates/${editingPlate.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ plateNumber: editingPlate.plateNumber, description: editingPlate.description, vehicleType: editingPlate.vehicleType, isBlacklist: editingPlate.isBlacklist, isActive: editingPlate.isActive })
      });
      if (!res.ok) throw new Error('Erro ao atualizar placa');
      const updated = await res.json();
      setPlates(prev => prev.map(p => p.id === updated.id ? updated : p));
      setEditingPlate(null);
      toast({ title: 'Placa atualizada', status: 'success', duration: 2000, isClosable: true });
    } catch (error) {
      toast({ title: 'Erro', description: error.message, status: 'error', duration: 3000, isClosable: true });
    }
  };

  const confirmRemove = (plate) => {
    setPlateToRemove(plate);
    onConfirmOpen();
  };

  const handleRemove = async () => {
    if (!plateToRemove) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/plates/${plateToRemove.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erro ao remover placa');
      setPlates(prev => prev.filter(p => p.id !== plateToRemove.id));
      setPlateToRemove(null);
      onConfirmClose();
      toast({ title: 'Placa removida', status: 'success', duration: 2000, isClosable: true });
    } catch (error) {
      toast({ title: 'Erro', description: error.message, status: 'error', duration: 3000, isClosable: true });
    }
  };

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return plates;
    return plates.filter((plate) => (
      (plate.plateNumber || plate.plate || '').toLowerCase().includes(term)
      || (plate.description || '').toLowerCase().includes(term)
      || (plate.vehicleType || '').toLowerCase().includes(term)
    ));
  }, [plates, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PLATES_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pagePlates = filtered.slice((safePage - 1) * PLATES_PER_PAGE, safePage * PLATES_PER_PAGE);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  return (
    <Box p={4} bg="white" borderRadius="md" boxShadow="sm">
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md" display="flex" alignItems="center">Placas Cadastradas</Heading>
        <HStack spacing={3} w="100%">
          <Input placeholder="Pesquisar placa, descrição ou tipo" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} flex="1" minW="220px" />
          <Button leftIcon={<RefreshIcon />} onClick={fetchPlates} isLoading={loading} minW="130px">Atualizar</Button>
          <Button colorScheme="teal" leftIcon={<AddIcon />} onClick={onOpen} minW="160px">Adicionar Placa</Button>
        </HStack>
      </Flex>

      {loading ? (
        <Flex justify="center" py={8}><Spinner size="lg" /></Flex>
      ) : filtered.length === 0 ? (
        <Box textAlign="center" py={8}><Text>Nenhuma placa cadastrada</Text></Box>
      ) : (
        <Table variant="simple">
          <Thead>
            <Tr><Th>Placa</Th><Th>Descrição</Th><Th>Tipo de Veículo</Th><Th>Blacklist</Th><Th>Ações</Th></Tr>
          </Thead>
          <Tbody>
            {pagePlates.map(p => (
              <Tr key={p.id}>
                <Td fontWeight="bold">{p.plateNumber || p.plate}</Td>
                <Td>{p.description}</Td>
                <Td>{p.vehicleType || '-'}</Td>
                <Td><Badge colorScheme={p.isBlacklist ? 'red' : 'gray'}>{p.isBlacklist ? 'Na Blacklist' : 'Normal'}</Badge></Td>
                <Td>
                  <HStack spacing={2}>
                    <Button size="sm" onClick={() => startEdit({ ...p })} leftIcon={<EditIcon />} minW="90px">Editar</Button>
                    <Button size="sm" colorScheme="red" onClick={() => confirmRemove(p)} leftIcon={<RemoveIcon />} minW="90px">Remover</Button>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      {!loading && filtered.length > PLATES_PER_PAGE && (
        <Flex justify="center" align="center" mt={4} gap={2} wrap="wrap">
          <IconButton
            icon={<ChevronLeftIcon />}
            size="sm"
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            isDisabled={safePage === 1}
            aria-label="Página anterior"
          />
          <Text fontSize="sm">
            Página {safePage} de {totalPages} ({filtered.length} placas)
          </Text>
          <IconButton
            icon={<ChevronRightIcon />}
            size="sm"
            onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
            isDisabled={safePage === totalPages}
            aria-label="Próxima página"
          />
        </Flex>
      )}

      {/* Modal adicionar */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Adicionar Placa</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3} isRequired>
              <FormLabel>Placa</FormLabel>
              <Input value={newPlate} onChange={(e) => setNewPlate(e.target.value.toUpperCase())} placeholder="AAA0000" />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>Descrição</FormLabel>
              <Input value={newDescription} onChange={(e) => setNewDescription(e.target.value)} placeholder="Razão do monitoramento" />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>Tipo de Veículo</FormLabel>
              <Input value={newVehicleType} onChange={(e) => setNewVehicleType(e.target.value)} placeholder="Ex: Táxi, Ônibus" />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onClose}>Cancelar</Button>
            <Button colorScheme="teal" onClick={handleAdd}>Adicionar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal editar simples implementado inline */}
      {editingPlate && (
        <Modal isOpen={!!editingPlate} onClose={() => setEditingPlate(null)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Editar Placa</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl mb={3} isRequired>
                <FormLabel>Placa</FormLabel>
                <Input value={editingPlate.plateNumber || editingPlate.plate} onChange={(e) => setEditingPlate(prev => ({ ...prev, plateNumber: e.target.value.toUpperCase() }))} />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>Descrição</FormLabel>
                <Input value={editingPlate.description || ''} onChange={(e) => setEditingPlate(prev => ({ ...prev, description: e.target.value }))} />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>Tipo de Veículo</FormLabel>
                <Input value={editingPlate.vehicleType || ''} onChange={(e) => setEditingPlate(prev => ({ ...prev, vehicleType: e.target.value }))} />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button mr={3} onClick={() => setEditingPlate(null)}>Cancelar</Button>
              <Button colorScheme="teal" onClick={handleEdit}>Salvar</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Confirma remoção */}
      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Confirmar remoção</AlertDialogHeader>
            <AlertDialogBody>
              Tem certeza que deseja remover a placa {plateToRemove?.plateNumber || plateToRemove?.plate}?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleRemove} ml={3}>Remover</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

    </Box>
  );
};

export default RegisteredPlatesTab;
